import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import useConfirm from "hooks/useConfirm";
import { reportError } from "utils/diagnostics";
import {
  CertificateRefreshStatus,
  getCertificateRefreshStatus,
  newCertificateRefreshRequestId,
  requestCertificateRefresh,
} from "./certificate-refresh.api";

const messages = {
  queued: "Refresh queued. Waiting for the device to download and validate the certificate.",
  running: "The device is refreshing its certificate.",
  succeeded: "The device confirmed that the certificate refresh succeeded.",
  failed: "The device reported that the certificate refresh failed. Check the reported reason before retrying.",
  unconfirmed: "No device confirmation has been received. This does not mean the certificate was replaced.",
};

const revocationLabels: Record<string, string> = {
  good: "Not revoked",
  revoked: "Revoked",
  unknown: "Unknown",
};

export default function CertificateRefreshPanel({ serialNumber }: { serialNumber: string }) {
  const [status, setStatus] = useState<CertificateRefreshStatus>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loadedAt, setLoadedAt] = useState(0);
  const [reloadMessage, setReloadMessage] = useState("");
  const [getConfirmation, Confirmation] = useConfirm();
  const live = useRef(true);
  const controller = useRef<AbortController>();
  const postController = useRef<AbortController>();
  const requestId = useRef<string>();
  const postInFlight = useRef(false);
  const getInFlight = useRef(false);

  const loadStatus = useCallback(
    async (background = false) => {
      if (postInFlight.current || (background && getInFlight.current)) return;
      controller.current?.abort();
      const current = new AbortController();
      controller.current = current;
      getInFlight.current = true;
      if (!background) {
        setLoading(true);
        setReloadMessage("");
      }
      try {
        const data = await getCertificateRefreshStatus(serialNumber, current.signal);
        if (!live.current || current.signal.aborted) return;
        if (data.serialNumber !== serialNumber) throw new Error("Certificate status device mismatch");
        setStatus(data);
        setError("");
        setLoadedAt(Date.now());
        if (!background) setReloadMessage("Certificate status reloaded.");
        if (requestId.current === data.request?.requestId) requestId.current = undefined;
      } catch (failure) {
        if (!live.current || current.signal.aborted) return;
        reportError("certificate.refresh.status", failure);
        setReloadMessage("");
        setError("Certificate status could not be loaded. Reload status before requesting another refresh.");
      } finally {
        if (controller.current === current) getInFlight.current = false;
        if (live.current && !current.signal.aborted) setLoading(false);
      }
    },
    [serialNumber],
  );

  useEffect(() => {
    live.current = true;
    loadStatus();
    return () => {
      live.current = false;
      controller.current?.abort();
      postController.current?.abort();
    };
  }, [loadStatus]);

  const state = status?.request?.state;
  const pending = state === "queued" || state === "running";
  const pollDelay = pending || requestId.current ? 5000 : 60000;
  useEffect(() => {
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      await loadStatus(true);
      if (!stopped) timer = window.setTimeout(poll, pollDelay);
    };
    timer = window.setTimeout(poll, pollDelay);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [loadStatus, pollDelay]);

  const refresh = async () => {
    if (!status?.supported || !status.online || pending || submitting || error) return;
    const confirmed = await getConfirmation({
      title: "Refresh Mission Control certificate?",
      content: `Device ${serialNumber} will fetch its certificate from the configured cloud source. Mission Control connections may briefly disconnect while its web server restarts.`,
      buttonTxt: "Refresh certificate",
    });
    if (!confirmed || !live.current) return;
    postInFlight.current = true;
    setSubmitting(true);
    setError("");
    setReloadMessage("");
    controller.current?.abort();
    controller.current = undefined;
    getInFlight.current = false;
    const current = new AbortController();
    postController.current = current;
    try {
      requestId.current = requestId.current || newCertificateRefreshRequestId();
      const data = await requestCertificateRefresh(serialNumber, requestId.current, current.signal);
      if (!live.current || current.signal.aborted) return;
      if (data.serialNumber !== serialNumber) throw new Error("Certificate status device mismatch");
      setStatus(data);
      setLoadedAt(Date.now());
      requestId.current = undefined;
    } catch (failure) {
      if (!live.current || current.signal.aborted) return;
      reportError("certificate.refresh.request", failure);
      const failureResponse = failure as { status?: number; data?: { error?: string }; response?: { status?: number } };
      if ([400, 403, 404, 409].includes(failureResponse?.status || failureResponse?.response?.status || 0)) {
        requestId.current = undefined;
        setError(
          `Refresh was not queued: ${
            failureResponse.data?.error || "request rejected"
          }. Reload status before retrying.`,
        );
      } else {
        setError(
          "The refresh request could not be confirmed. Reload status before retrying; it may already be queued.",
        );
      }
    } finally {
      postInFlight.current = false;
      if (live.current && !current.signal.aborted) {
        setSubmitting(false);
        setLoading(false);
      }
    }
  };

  return (
    <Box component="section" aria-label="Mission Control certificate" sx={{ mt: 3, p: 2 }}>
      <Typography variant="h6">Mission Control certificate</Typography>
      <Typography>Device: {serialNumber}</Typography>
      {status?.report && (
        <>
          <Typography>Certificate serial: {status.report.certificate.serial || "Unknown"}</Typography>
          <Typography>Expires: {status.report.certificate.notAfter || "Unknown"}</Typography>
          <Typography>Revocation status: {revocationLabels[status.report.revocation.status] || "Unknown"}</Typography>
          {status.report.revocation.checkedAt > 0 && (
            <Typography>
              Last revocation check: {new Date(status.report.revocation.checkedAt * 1000).toLocaleString()}
            </Typography>
          )}
          <Typography>Last device report: {new Date(status.reportedAt * 1000).toLocaleString()}</Typography>
        </>
      )}
      {status && !status.supported && (
        <Alert severity="info">
          Install firmware with remote certificate-refresh support before using this action.
        </Alert>
      )}
      {status?.supported && !status.online && (
        <Alert severity="warning">The device is offline or its certificate report is stale.</Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Button
          onClick={refresh}
          disabled={!status?.supported || !status.online || pending || submitting || loading || Boolean(error)}
        >
          {submitting ? "Requesting refresh..." : "Refresh certificate"}
        </Button>
        <Button onClick={() => loadStatus()} disabled={loading || submitting}>
          {loading ? "Loading certificate status..." : "Reload certificate status"}
        </Button>
      </Box>
      {loadedAt > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Status last loaded: {new Date(loadedAt).toLocaleString()}
        </Typography>
      )}
      {reloadMessage && (
        <Typography role="status" variant="body2" sx={{ mt: 1 }}>
          {reloadMessage}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Reload retrieves the latest device report. The revocation-check time changes when the device runs a check.
      </Typography>
      {state && (
        <Alert sx={{ mt: 2 }} severity={state === "succeeded" ? "success" : state === "failed" ? "error" : "info"}>
          {messages[state] || "Certificate refresh status is unknown."}
          {status?.request?.reason ? ` Reason: ${status.request.reason}.` : ""}
        </Alert>
      )}
      <Confirmation />
    </Box>
  );
}
